import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from "./ui/button";
import { Play, Pause, RotateCcw, Settings, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DEFAULT_ROUND_TIME_MINUTES = 5.17; // 5 minutes and 10 seconds (5 + 10/60)
const LOCKOUT_DURATION = 5000; // 5 seconds lockout
const WARNING_TIME = 5; // Visual warning at 5 seconds
const ALARM_SOUND = "/Alarm Sound Effect.mp3";

export default function Home() {
  const [roundTimeMinutes, setRoundTimeMinutes] = useState(DEFAULT_ROUND_TIME_MINUTES);
  const [timeLeft, setTimeLeft] = useState(() => Math.floor(DEFAULT_ROUND_TIME_MINUTES * 60));
  const [isRunning, setIsRunning] = useState(false);
  const [team1Score, setTeam1Score] = useState(0);
  const [team2Score, setTeam2Score] = useState(0);
  const [team1Locked, setTeam1Locked] = useState(false);
  const [team2Locked, setTeam2Locked] = useState(false);
  const [hasPlayedAlarmSound, setHasPlayedAlarmSound] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInputValue, setSettingsInputValue] = useState(DEFAULT_ROUND_TIME_MINUTES.toString());
  const alarmAudioRef = useRef(null);

  // Update timeLeft when roundTimeMinutes changes (only if timer hasn't reached 0)
  useEffect(() => {
    const newTime = Math.floor(roundTimeMinutes * 60);
    // Only update timeLeft if timer hasn't reached 0 yet
    // This prevents the timer from resetting when it reaches 00:00
    setTimeLeft((currentTime) => {
      // If timer is at 0, keep it at 0 (don't reset automatically)
      if (currentTime === 0) {
        return 0;
      }
      // Otherwise update to new time (when settings change)
      return newTime;
    });
  }, [roundTimeMinutes]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer logic
  useEffect(() => {
    let interval = null;
    
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 0) {
            setIsRunning(false);
            return 0;
          }
          
          const newTime = prev - 1;
          
          // Play alarm sound when timer reaches 0
          if (newTime === 0 && !hasPlayedAlarmSound && alarmAudioRef.current) {
            const playAlarmSound = () => {
              if (alarmAudioRef.current) {
                alarmAudioRef.current.src = ALARM_SOUND;
                alarmAudioRef.current.load();
                
                const playPromise = alarmAudioRef.current.play();
                
                if (playPromise !== undefined) {
                  playPromise
                    .then(() => {
                      // Alarm sound started playing successfully
                    })
                    .catch((error) => {
                      console.log('Alarm sound play failed, retrying...', error);
                      setTimeout(() => {
                        if (alarmAudioRef.current) {
                          alarmAudioRef.current.play().catch(() => {});
                        }
                      }, 100);
                    });
                }
              }
            };
            
            setTimeout(playAlarmSound, 50);
            setHasPlayedAlarmSound(true);
            setIsRunning(false);
            return 0;
          }
          
          if (newTime === 0) {
            setIsRunning(false);
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      clearInterval(interval);
    };
  }, [isRunning, hasPlayedAlarmSound, timeLeft]);

  // Team 1 goal handler
  const handleTeam1Goal = useCallback(() => {
    if (team1Locked) return;
    // If timer is 0, allow goals without timer running
    if (timeLeft > 0 && !isRunning) return;
    
    setTeam1Score((prev) => prev + 1);
    setTeam1Locked(true);
    setTeam2Locked(true);
    
    setTimeout(() => {
      setTeam1Locked(false);
      setTeam2Locked(false);
    }, LOCKOUT_DURATION);
  }, [team1Locked, isRunning, timeLeft]);

  // Team 2 goal handler
  const handleTeam2Goal = useCallback(() => {
    if (team2Locked) return;
    // If timer is 0, allow goals without timer running
    if (timeLeft > 0 && !isRunning) return;
    
    setTeam2Score((prev) => prev + 1);
    setTeam1Locked(true);
    setTeam2Locked(true);
    
    setTimeout(() => {
      setTeam1Locked(false);
      setTeam2Locked(false);
    }, LOCKOUT_DURATION);
  }, [team2Locked, isRunning, timeLeft]);

  // Reset handler
  const handleReset = () => {
    setTimeLeft(Math.floor(roundTimeMinutes * 60));
    setIsRunning(false);
    setTeam1Score(0);
    setTeam2Score(0);
    setTeam1Locked(false);
    setTeam2Locked(false);
    setHasPlayedAlarmSound(false);
    // Reset audio element to ensure it's ready for next play
    if (alarmAudioRef.current) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
    }
  };

  // Settings handlers
  const handleOpenSettings = () => {
    setSettingsInputValue(roundTimeMinutes.toString());
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleSaveSettings = () => {
    const minutes = parseFloat(settingsInputValue) || 0;
    if (minutes < 0) {
      setSettingsInputValue('0');
      return;
    }
    setRoundTimeMinutes(minutes);
    setIsSettingsOpen(false);
    // Reset timer with new time
    if (!isRunning) {
      setTimeLeft(Math.floor(minutes * 60));
    }
  };

  // Initialize audio on first user interaction (helps with iOS autoplay restrictions)
  const initializeAudio = useCallback(() => {
    // Initialize alarm audio
    if (alarmAudioRef.current) {
      const alarmPlayPromise = alarmAudioRef.current.play();
      if (alarmPlayPromise !== undefined) {
        alarmPlayPromise
          .then(() => {
            alarmAudioRef.current.pause();
            alarmAudioRef.current.currentTime = 0;
          })
          .catch(() => {
            // Ignore errors during initialization
          });
      }
    }
  }, []);

  // Toggle timer
  const toggleTimer = () => {
    // If timer is at 0, reset everything when clicking start
    if (timeLeft === 0 && !isRunning) {
      // Reset everything first
      setTimeLeft(Math.floor(roundTimeMinutes * 60));
      setIsRunning(false);
      setTeam1Score(0);
      setTeam2Score(0);
      setTeam1Locked(false);
      setTeam2Locked(false);
      setHasPlayedAlarmSound(false);
      
      // Initialize audio and start timer after a small delay to ensure reset is complete
      initializeAudio();
      setTimeout(() => {
        setIsRunning(true);
      }, 100);
      return;
    }
    
    if (timeLeft === 0) return;
    
    // Initialize audio on first start (helps with older iOS devices)
    if (!isRunning) {
      initializeAudio();
    }
    setIsRunning(!isRunning);
  };

  const hasTimer = roundTimeMinutes > 0;

  const isLocked = team1Locked || team2Locked;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col items-center justify-center p-4 overflow-hidden relative">
      {/* Audio element for alarm sound when timer reaches 0 */}
      <audio 
        ref={alarmAudioRef} 
        preload="auto"
        playsInline
        webkit-playsinline="true"
      >
        <source src={ALARM_SOUND} type="audio/mpeg" />
        <source src={ALARM_SOUND} type="audio/mp3" />
      </audio>

      {/* Settings button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={handleOpenSettings}
        className="fixed top-4 left-4 p-3 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all duration-200 z-50"
        aria-label="Innstillinger"
      >
        <Settings className="w-5 h-5" />
      </motion.button>

      {/* Version number */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed top-4 right-4 text-white/40 text-xs font-mono z-50"
      >
        v1.0.0
      </motion.div>

      {/* Settings Modal */}
      <AnimatePresence>
        {isSettingsOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseSettings}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-slate-800 rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-white">Innstillinger</h2>
                  <button
                    onClick={handleCloseSettings}
                    className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                    aria-label="Lukk"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Tid per runde (minutter)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settingsInputValue}
                    onChange={(e) => setSettingsInputValue(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="mt-2 text-xs text-white/50">
                    Sett til 0 for å kun telle mål uten timer
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSaveSettings}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Lagre
                  </Button>
                  <Button
                    onClick={handleCloseSettings}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-light tracking-[0.3em] text-white/60 uppercase">
          Innebandy
        </h1>
      </motion.div>

      {/* Timer Display */}
      {hasTimer && (
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative mb-8"
        >
          <div className={`
            text-7xl md:text-9xl font-mono font-bold tracking-tight
            ${timeLeft === 0 ? 'text-red-600' : timeLeft <= WARNING_TIME && timeLeft > 0 ? 'text-red-500 animate-pulse' : 'text-white'}
            transition-colors duration-300
          `}>
            {formatTime(timeLeft)}
          </div>
          
          {/* Lockout indicator */}
          <AnimatePresence>
            {isLocked && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute -bottom-6 left-0 right-0 text-center text-amber-400 text-sm font-medium tracking-wider whitespace-nowrap"
              >
                MÅL REGISTRERT
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Scoreboard */}
      <div className="w-full max-w-4xl grid grid-cols-3 gap-4 md:gap-8 items-center mb-12">
        {/* Team 1 */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center"
        >
          <span className="text-blue-400 text-sm md:text-base font-medium tracking-widest uppercase mb-2">
            Lag 1
          </span>
          <motion.button
            whileTap={{ scale: isLocked || (hasTimer && !isRunning) ? 1 : 0.95 }}
            onClick={handleTeam1Goal}
            disabled={team1Locked || (hasTimer && !isRunning)}
            className={`
              relative w-28 h-28 md:w-40 md:h-40 rounded-2xl
              flex items-center justify-center
              text-5xl md:text-7xl font-bold text-white
              transition-all duration-300 cursor-pointer
              ${team1Locked 
                ? 'bg-blue-900/30 border-2 border-blue-500/30' 
                : 'bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 shadow-lg shadow-blue-500/25'
              }
              ${hasTimer && !isRunning ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {team1Score}
            {team1Locked && (
              <div className="absolute inset-0 rounded-2xl border-2 border-blue-400 animate-ping opacity-30" />
            )}
          </motion.button>
          <span className="text-white/40 text-xs mt-3 h-4">
            {team1Locked ? 'Venter...' : (hasTimer ? (isRunning ? 'Trykk for mål' : '') : 'Trykk for mål')}
          </span>
        </motion.div>

        {/* VS Divider */}
        <div className="flex flex-col items-center justify-center">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
          <span className="text-white/30 text-xl font-light my-2">vs</span>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
        </div>

        {/* Team 2 */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center"
        >
          <span className="text-orange-400 text-sm md:text-base font-medium tracking-widest uppercase mb-2">
            Lag 2
          </span>
          <motion.button
            whileTap={{ scale: isLocked || (hasTimer && !isRunning) ? 1 : 0.95 }}
            onClick={handleTeam2Goal}
            disabled={team2Locked || (hasTimer && !isRunning)}
            className={`
              relative w-28 h-28 md:w-40 md:h-40 rounded-2xl
              flex items-center justify-center
              text-5xl md:text-7xl font-bold text-white
              transition-all duration-300 cursor-pointer
              ${team2Locked 
                ? 'bg-orange-900/30 border-2 border-orange-500/30' 
                : 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 shadow-lg shadow-orange-500/25'
              }
              ${hasTimer && !isRunning ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {team2Score}
            {team2Locked && (
              <div className="absolute inset-0 rounded-2xl border-2 border-orange-400 animate-ping opacity-30" />
            )}
          </motion.button>
          <span className="text-white/40 text-xs mt-3 h-4">
            {team2Locked ? 'Venter...' : (hasTimer ? (isRunning ? 'Trykk for mål' : '') : 'Trykk for mål')}
          </span>
        </motion.div>
      </div>

      {/* Controls */}
      <motion.div 
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex gap-4"
      >
        {hasTimer && (
          <Button
            onClick={toggleTimer}
            size="lg"
            className={`
              px-8 py-6 rounded-xl text-base font-medium tracking-wide
              transition-all duration-300
              ${isRunning 
                ? 'bg-amber-500 hover:bg-amber-400 text-black' 
                : 'bg-emerald-500 hover:bg-emerald-400 text-black'
              }
            `}
          >
            {isRunning ? (
              <>
                <Pause className="w-5 h-5 mr-2" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start
              </>
            )}
          </Button>
        )}

        <Button
          onClick={handleReset}
          size="lg"
          variant="outline"
          className="px-8 py-6 rounded-xl text-base font-medium tracking-wide border-white/20 text-white hover:bg-white/10 hover:text-white"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Reset
        </Button>
      </motion.div>


      {/* Decorative elements */}
      <div className="fixed top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />
    </div>
  );
}
